import type { StatusEffect } from './types'

// ---------------------------------------------------------------------------
// Effets de statut — gel, brûlure, paralysie, sommeil, poison.
// La configuration est PURE (aucune randomness ici): le moteur de combat
// (battle.ts) l'applique de façon 100% déterministe, donc les effets sont
// rejouables et vérifiables par le résolveur d'arène comme le reste.
// ---------------------------------------------------------------------------

export interface StatusMeta {
  label: string
  emoji: string
  /** Verbe d'état, ex. « gelé » — pour le log. */
  adj: string
}

export const STATUS_INFO: Record<StatusEffect, StatusMeta> = {
  burn: { label: 'Brûlure', emoji: '🔥', adj: 'brûlé' },
  freeze: { label: 'Gel', emoji: '🧊', adj: 'gelé' },
  paralyze: { label: 'Paralysie', emoji: '⚡', adj: 'paralysé' },
  sleep: { label: 'Sommeil', emoji: '😴', adj: 'endormi' },
  poison: { label: 'Poison', emoji: '☠️', adj: 'empoisonné' },
}

export interface StatusConfig {
  /** Durée en tours quand l'effet s'applique. */
  turns: number
  /** Dégâts de fin de tour, en fraction des HP max (0 = pas de DoT). */
  dotPct: number
  /**
   * Empêche d'agir: 'always' (endormi), 'chance' (gel/paralysie — probabilité
   * de rester bloqué ce tour), ou 'none' (brûlure/poison n'empêchent pas d'agir).
   */
  skip: 'always' | 'chance' | 'none'
  skipChance?: number
}

export const STATUS_CFG: Record<StatusEffect, StatusConfig> = {
  // brûlure/poison: pas de blocage, mais grignotent les HP chaque tour
  burn: { turns: 3, dotPct: 0.06, skip: 'none' },
  poison: { turns: 3, dotPct: 0.05, skip: 'none' },
  // gel: peut figer complètement l'action (forte proba), courte durée
  freeze: { turns: 2, dotPct: 0, skip: 'chance', skipChance: 0.65 },
  // sommeil: bloque toujours, jusqu'au réveil
  sleep: { turns: 2, dotPct: 0, skip: 'always' },
  // paralysie: gêne l'action de temps en temps, sur la durée
  paralyze: { turns: 3, dotPct: 0, skip: 'chance', skipChance: 0.3 },
}

/** Effets disponibles selon le type de coup — les plus punitifs sur les spéciales. */
export const EFFECT_POOL_SPECIAL: StatusEffect[] = ['burn', 'freeze', 'paralyze', 'sleep', 'poison']
export const EFFECT_POOL_NORMAL: StatusEffect[] = ['burn', 'paralyze', 'poison']
